import type { BirdKind } from '../../../shared/game/world-types.js';
import type { Habitat } from './critter-ground.js';

export type { BirdKind };

/** Everything that makes one kind of bird look and behave differently from another; the model and brain are shared. */
export interface BirdSpecies {
  kind: BirdKind;
  /** Where the species spawns and walks; see `CritterGround.welcome`. */
  habitat: Habitat;
  /** The multiple of real size it is drawn at, so it reads from the isometric camera. */
  scale: number;
  plumage: { body: number; back: number; wing: number; tip: number; beak: number; legs: number; sheen: number; eye: number; spot?: number };
  /** Proportions relative to a crow: wing length and breadth, and beak length. */
  build: { wing: number; chord: number; beak: number };
  /** Metres at which it notices something worth leaving for, how fast it walks, the length of a stride cycle and its cruising speed (m/s). */
  scareReach: number; walkSpeed: number; strideLength: number; cruiseSpeed: number;
  /** Wingbeat rate in radians per second, as [at a glide, extra at full effort]. */
  flapRate: [number, number];
  /** How much of the time in flight is spent gliding, 0–1: a crow flaps on, a gull rides the air. */
  glide: number;
  /** Stretches its wings now and then while standing about. */
  stretches: boolean;
}

export const CROW: BirdSpecies = {
  kind: 'crow', habitat: 'crow', scale: 1.2,
  plumage: { body: 0x2c282e, back: 0x222027, wing: 0x2a2630, tip: 0x19171c, beak: 0x1c1b1e, legs: 0x1d1b1f, sheen: 0x3a3a52, eye: 0xb9a98a },
  build: { wing: 1, chord: 1, beak: 1 },
  scareReach: 8, walkSpeed: 1.1, strideLength: .62, cruiseSpeed: 7.5, flapRate: [12, 5], glide: .6, stretches: false,
};

/** A herring gull: white head and underparts, pale grey back and wings with black tips, a yellow bill with a red spot, pink-orange legs. */
export const GULL: BirdSpecies = {
  kind: 'gull', habitat: 'gull', scale: 1.3,
  plumage: { body: 0xece6dc, back: 0xb9bcc0, wing: 0xc4c6c8, tip: 0x2a2a2e, beak: 0xe0a531, legs: 0xd98a4a, sheen: 0xc9ccd0, eye: 0x4a3b22, spot: 0xc9402f },
  build: { wing: 1.3, chord: .9, beak: 1.2 },
  scareReach: 7, walkSpeed: .9, strideLength: .5, cruiseSpeed: 7, flapRate: [8, 3], glide: .9, stretches: true,
};

export const BIRDS: Record<BirdKind, BirdSpecies> = { crow: CROW, gull: GULL };
